// src/App.jsx
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Guards
import RequireRole from "./components/auth/RequireRole";
import RequireFeeAuth from "./components/auth/RequireFeeAuth";

// Auth
import Login from "./pages/auth/Login";

// Superadmin pages
import Dashboard from "./pages/superadmin/Dashboard";
import ManageSchools from "./pages/superadmin/ManageSchools";
import ManageUsers from "./pages/superadmin/ManageUsers";
import Settings from "./pages/superadmin/Settings";

// Superadmin layout
import AdminNavbar from "./components/Layout/AdminNavbar";
import AdminSidebar from "./components/Layout/AdminSidebar";

// School admin pages/layout
import SchoolLayout from "./pages/school/SchoolLayout";
import SchoolAdminDashboard from "./pages/school/SchoolAdminDashboard";
import SchoolProfile from "./pages/school/SchoolProfile";
import Students from "./pages/school/Students";
import Teachers from "./pages/school/Teachers";
import OnlineClasses from "./pages/school/Onlineclasses";
import AssignmentsPage from "./pages/school/Assingments";
import Attendance from "./pages/school/Attendance";
import CircularsPage from "./pages/school/Circulars";
import ExamTemplate from "./pages/school/Exam/ExamTemplate";
import ExamsPage from "./pages/school/Exam/ExamsPage";
import TemplateQuestions from "./pages/school/Exam/TemplateQuestions";
import QuestionBankPage from "./pages/school/Exam/QuestionBankPage";
import CreateQuestionBank from "./pages/school/CreateQuestionBank";
import SchoolSettings from "./pages/school/Settings";

// Fees module
import FeeLogin from "./pages/school/fees/FeeLogin";
import FeesDashboard from "./pages/school/fees/FeesDashboard";
import FeesCollect from "./pages/school/fees/FeesCollect";
import FeesFix from "./pages/school/fees/FeesFix";

import Support from "./pages/school/Support";

// Student area
import StudentLayout from "./pages/student/StudentLayout";
import StudentDashboard from "./pages/student/StudentDashboard";
import Profile from "./pages/student/Profile";
import OnlineClass from "./pages/student/OnlineClass";
import StudentAssingments from "./pages/student/StudentAssingments";
import StudentAttendance from "./pages/student/StudentAttendance";
import StudentCircular from "./pages/student/StudentCircular";
import StudentSettings from "./pages/student/StudentSettings";
import StudentSupport from "./pages/student/StudentSupport";

// Brand (scoped theme per section/user)
import { BrandProvider } from "./pages/student/_brand";
import StudentExamsPage from "./pages/student/exams/StudentExamsPage";
import WriteExam from "./pages/student/exams/WriteExam";
import StudentResults from "./pages/student/exams/StudentResults";
import StudentExamView from "./pages/student/exams/StudentExamView";
import ProgressCard from "./pages/student/ProgressCard";

// Teacher
import TeacherLayout from "./pages/teacher/TeacherLayout";
import TeacherDashboard from "./pages/teacher/TeacherDashboard";
import TeacherOnlineClasses from "./pages/teacher/TeacherOnlineClasses";
import TeacherAssingments from "./pages/teacher/TeacherAssingments";
import TeacherAttandance from "./pages/teacher/TeacherAttandance";
import TeacherCirculars from "./pages/teacher/TeacherCirculars";
import TeacherProfile from "./pages/teacher/TeacherProfile";
import TeacherExamPage from "./pages/teacher/exams/TeacherExamPage";
import TeacherExamTemplate from "./pages/teacher/exams/TeacherExamTemplate";
import TeacherQuestionBank from "./pages/teacher/exams/TeacherQuestionBank";
import TeacherTemplateCreate from "./pages/teacher/exams/TeacherTemplateCreate";
import TeacherExamVIew from "./pages/teacher/exams/TeacherExamVIew";

// ✅ Dynamic title & favicon
import { useSchool } from "./context/SchoolProvider";
import DynamicHead from "./components/DynamicHead";
import StudentResultsPage from "./pages/school/Exam/StudentResultsPage";
import TeacherStudentResultsDashboard from "./pages/teacher/TeacherStudentResultsDashboard";

/* ---------- Small shells to scope brand per section ---------- */
const AdminShell = ({ children }) => (
  <div>
    <AdminNavbar />
    <div style={{ display: "flex" }}>
      <AdminSidebar />
      <div style={{ flex: 1, padding: 16 }}>{children}</div>
    </div>
  </div>
);

// School shell → brand namespace by schoolId
const SchoolShellBrand = () => {
  const { profile } = useAuth();
  const ns = `school:${profile?.schoolId || "unknown"}`;
  return (
    <div id="school-root">
      <BrandProvider key={ns} ns={ns} scope="#school-root">
        <SchoolLayout />
      </BrandProvider>
    </div>
  );
};

// Student shell → brand namespace by user uid
const StudentShellBrand = () => {
  const { user } = useAuth();
  const ns = `student:${user?.uid || "guest"}`;
  return (
    <div id="student-root">
      <BrandProvider key={ns} ns={ns} scope="#student-root">
        <StudentLayout />
      </BrandProvider>
    </div>
  );
};

/* Standalone write-exam shell (NO StudentLayout, brand only) */
const ExamWriteShell = () => {
  const { user } = useAuth();
  const ns = `student:${user?.uid || "guest"}`;
  return (
    <div id="student-root">
      <BrandProvider key={ns} ns={ns} scope="#student-root">
        <WriteExam />
      </BrandProvider>
    </div>
  );
};

/* Standalone view-exam shell (NO StudentLayout, brand only) */
const ExamViewShell = () => {
  const { user } = useAuth();
  const ns = `student:${user?.uid || "guest"}`;
  return (
    <div id="student-root">
      <BrandProvider key={ns} ns={ns} scope="#student-root">
        <StudentExamView />
      </BrandProvider>
    </div>
  );
};

/* ---------- Tiny layout for /school/exams ---------- */
const ExamsShell = () => <Outlet />;

/* ---------- Root redirect ---------- */
const RootRedirect = () => {
  const { user, profile, loading } = useAuth();

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const rawRole = String(profile?.role || "").toLowerCase();
  const role =
    rawRole === "admin"
      ? "superadmin"
      : rawRole === "school" || rawRole === "school_admin"
      ? "schooladmin"
      : rawRole;

  if (role === "superadmin") return <Navigate to="/admin" replace />;
  if (role === "schooladmin") return <Navigate to="/school" replace />;
  if (role === "student") return <Navigate to="/student" replace />;

  return <Navigate to="/login" replace />;
};

/* ---------- Head bridge to inject dynamic title & favicon --------- */
const HeadBridge = () => {
  const { school } = useSchool();
  return <DynamicHead school={school} />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Applies school name + favicon for all routes/roles */}
        <HeadBridge />

        <Routes>
          {/* Root -> redirect by role */}
          <Route path="/" element={<RootRedirect />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />

          {/* Superadmin area */}
          <Route
            path="/admin"
            element={
              <RequireRole role="superadmin">
                <AdminShell>
                  <Dashboard />
                </AdminShell>
              </RequireRole>
            }
          />
          <Route
            path="/admin/schools"
            element={
              <RequireRole role="superadmin">
                <AdminShell>
                  <ManageSchools />
                </AdminShell>
              </RequireRole>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequireRole role="superadmin">
                <AdminShell>
                  <ManageUsers />
                </AdminShell>
              </RequireRole>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RequireRole role="superadmin">
                <AdminShell>
                  <Settings />
                </AdminShell>
              </RequireRole>
            }
          />

          {/* School Admin area (scoped brand) */}
          <Route
            path="/school"
            element={
              <RequireRole role="schooladmin">
                <SchoolShellBrand />
              </RequireRole>
            }
          >
            <Route index element={<SchoolAdminDashboard />} />
            <Route path="profile" element={<SchoolProfile />} />
            <Route path="students" element={<Students />} />
            <Route path="teachers" element={<Teachers />} />
            <Route path="onlineclasses" element={<OnlineClasses />} />
            <Route path="assignments" element={<AssignmentsPage />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="circulars" element={<CircularsPage />} />
            <Route path="settings" element={<SchoolSettings />} />
            <Route path="support" element={<Support />} />

            {/* -------------------- FEES MODULE -------------------- */}
            {/* PIN Login (unguarded) */}
            <Route path="fees/login" element={<FeeLogin />} />

            {/* Guarded subtree — FeesDashboard acts as layout and renders its own <Outlet /> */}
            <Route
              path="fees"
              element={
                <RequireFeeAuth>
                  <FeesDashboard />
                </RequireFeeAuth>
              }
            >
              <Route path="collect" element={<FeesCollect />} />
              <Route path="fix" element={<FeesFix />} />
            </Route>
            {/* ------------------ end FEES MODULE ------------------ */}

            {/* --------------------- Exams subtree --------------------- */}
            <Route path="exams" element={<ExamsShell />}>
              <Route path="studentresults" element={<StudentResultsPage />} />
              <Route index element={<ExamsPage />} />
              <Route path="question-bank">
                <Route index element={<QuestionBankPage />} />
                <Route path="new" element={<CreateQuestionBank />} />
              </Route>
              <Route path="templates">
                <Route path="new" element={<ExamTemplate />} />
                <Route path=":id" element={<ExamTemplate />} />
                <Route path="question-bank" element={<QuestionBankPage />} />
                <Route path=":id/questions" element={<TemplateQuestions />} />
              </Route>
            </Route>
            {/* ------------------- end Exams subtree ------------------- */}
          </Route>

          {/* Teacher area */}
          <Route
            path="/teacher"
            element={
              <RequireRole role="teacher">
                <TeacherLayout />
              </RequireRole>
            }
          >
            <Route index element={<TeacherDashboard />} />
            <Route path="onlineclasses" element={<TeacherOnlineClasses />} />
            <Route path="assignments" element={<TeacherAssingments />} />
            <Route path="attendance" element={<TeacherAttandance />} />
            <Route path="circulars" element={<TeacherCirculars />} />
            <Route path="profile" element={<TeacherProfile />} />
             <Route path="results-dashboard" element={<TeacherStudentResultsDashboard />} />
            <Route path="exams/examspage" element={<TeacherExamPage />} />
            <Route path="exams/templates/create" element={<TeacherTemplateCreate />} />
            <Route path="exams/templates/:id" element={<TeacherExamTemplate />} />
            <Route path="exams/templates/:id/questions" element={<TeacherQuestionBank />} />
            <Route path="exams/templates/:id/view" element={<TeacherExamVIew />} />
          </Route>

          {/* Student area (scoped brand) */}
          <Route
            path="/student"
            element={
              <RequireRole role="student">
                <StudentShellBrand />
              </RequireRole>
            }
          >
            <Route index element={<StudentDashboard />} />
            <Route path="profile" element={<Profile />} />
            <Route path="classes" element={<OnlineClass />} />
            <Route path="assignments" element={<StudentAssingments />} />
            <Route path="attendance" element={<StudentAttendance />} />
            <Route path="exams/examspage" element={<StudentExamsPage />} />
            <Route path="exams/:examId/results" element={<StudentResults />} />
            <Route path="progresscard" element={<ProgressCard />} />
            <Route path="notices" element={<StudentCircular />} />
            <Route path="support" element={<StudentSupport />} />
            <Route path="settings" element={<StudentSettings />} />
          </Route>

          {/* Standalone exam pages – NO StudentLayout, but keep brand */}
          <Route
            path="/student/exams/:examId/write"
            element={
              <RequireRole role="student">
                <ExamWriteShell />
              </RequireRole>
            }
          />
          <Route
            path="/student/exams/:examId/view"
            element={
              <RequireRole role="student">
                <ExamViewShell />
              </RequireRole>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
